import { Flex } from '@ui-library/misc';
import React, { useEffect } from 'react';
import Styled from 'styled-components';
import theme from '@css/theme';
import { LabelDiv, Text } from '@ui-library/typography';
import { startCase } from 'lodash';
import {
  InfoCircleOutlined,
  WarningTwoTone,
  PauseCircleTwoTone,
  SyncOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { DateFormat } from '@ui-library/pickers/DateFormat';
import Tooltip from '@ui-library/tooltip';
import CountUp from 'react-countup';
import { useCloudSourceInfo } from '@stores/cloudSourcesInfo/cloudSourcesInfo';
import { isSyncing } from '@stores/cloudSourcesInfo/util';
import { FixMe } from '@utils/types';
import { humanizeRunDatesDuration } from '../cloudSourceStatusTab/StatusTable';

import classes from './sync-report-common.module.scss';
import { MetricProps } from './metricModal';
import { UnsuccessfulEventErrorProps } from './errors';
import SyncBehaviourModeFactory from './syncBehaviourModeFactory';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const BaseContainer = Styled(Flex)`
  width:100%;
  padding:12px 24px;
  border-radius:6px;
  position: relative;
  overflow:hidden;
  background-color:#ffffff;
  height:90%;
`;

const DropShadow = Styled.div`
  filter: drop-shadow(0px 2px 5px rgb(0 0 0 / 15%));
  margin-bottom:12px;
  height:100%;
`;
enum SyncResult {
  Success = 'success',
  Error = 'error',
  Syncing = 'syncing',
  Paused = 'paused',
  RecordsSentToDestination = 'RecordsSentToDestination',
}

const ResultBorder = Styled.div<{ result: SyncResult }>`
  position:absolute;
  width:6px;
  background: ${({ result }: FixMe) => (result === 'syncing' ? () => theme.color.rsPrimary : '')};
  background: ${({ result }: FixMe) =>
    result === SyncResult.RecordsSentToDestination ? () => '#33cccc' : ''};
  background: ${({ result }: FixMe) => (result === 'success' ? () => theme.color.success : '')};
  background: ${({ result }: FixMe) => (result === 'error' ? () => '#EDCB74' : '')};
  background: ${({ result }: FixMe) => (result === 'paused' ? () => '#ff5050' : '')};
  top:0;
  left:0;
  bottom:0;
`;

const ArrowContainer = Styled(BaseContainer)`
clip-path: polygon(0% 0%, 97% 0, 100% 50%, 97% 100%, 0% 100%);`;

const syncTypeInfo: { [i: string]: string } = {
  incremental:
    'Incremental sync action refers to syncing only new additions in the warehouse model, to the destination.',
  full: 'Full sync action refers to syncing the entire warehouse model to the destination.',
  // upsert: 'Upsert Mode for de-dup',
};

export const METRIC_TYPE = {
  DELTAS: 'deltas',
  SUCCESSFUL: 'successful',
};

const SYNC_DIVIDER = {
  SOURCES: 'sources',
  SERVER: 'server',
};

export interface IReport {
  sourceStats: {
    totalCount: number;
    succeeded: number;
    invalid: number;
    changed?: number;
  };
  serverStats: {
    totalCount: number;
    succeeded: number;
    rejected: number;
  };
  opMetrics?: {
    total?: {
      inserts: number;
      deletes: number;
      updates: number;
    };
    successful?: {
      inserts: number;
      deletes: number;
      updates: number;
    };
  };
  jobId: string;
  jobRunId: string;
  taskId: string;
  taskRunId: string;
  error: string;
  startedAt: string;
  finishedAt: string;
  triggerSync: 'manual' | 'auto'; // auto default
  syncType: 'incremental' | 'full' | 'Upsert'; // full default
  state?: string;
  progress?: string;
  stateObj?: {
    type: SyncResult;
    info: string;
  };
}

interface MainContainerPropsProps {
  type: 'sources' | 'server';
  arrow?: boolean;
  succeeded: number;
  totalCount?: number;
  deltas?: number;
  rejected: number;
  startedAt: string;
  finishedAt?: string;
  triggerSync?: 'auto' | 'manual';
  onClickErrors?: (type: 'sources' | 'server') => void;
  onClickMetric?: (type: string) => void;
  syncType?: 'incremental' | 'full' | 'Upsert';
  error: string;
  state?: string;
  progress?: string;
  showResultFeedback?: boolean;
  stateObj?: {
    type: SyncResult;
    info: string;
  };
}

interface ReportProps {
  report: IReport;
  operationMetrices?: MetricProps;
  onClickRejected: (data: UnsuccessfulEventErrorProps) => void;
  onClickRecords?: (data: MetricProps) => void;
}
interface ISourceInfoBlockProps {
  totalCount: number;
  deltas?: number;
  onClickMetric?: (type: 'deltas' | 'successful') => void;
}
const SourceInfoBlock: React.FC<ISourceInfoBlockProps> = ({
  totalCount,
  onClickMetric,
  deltas,
}: ISourceInfoBlockProps) => (
  <Flex style={{ marginLeft: 'auto' }}>
    <InfoBlock count={totalCount || 0} type="Rows in source" noLink noBackground />
    <InfoBlock
      count={deltas || 0}
      type="Deltas"
      noLink={!onClickMetric}
      noBackground={!onClickMetric}
      onClick={
        onClickMetric &&
        (() => {
          onClickMetric('deltas');
        })
      }
    />
  </Flex>
);

const InfoBlock = ({
  count,
  type,
  onClick,
  noBackground,
  noLink,
  widthI,
}: {
  count: number;
  type: string;
  onClick?: () => void;
  noBackground?: boolean;
  noLink?: boolean;
  widthI?: string;
}) => (
  <Flex
    flexDirection="row"
    style={{
      marginRight: 8,
      width: widthI || '120px',
      padding: '4px 8px',
      borderRadius: 4,
      border: !noBackground && count ? '1px solid #dedede' : 'unset',
      backgroundColor: !noBackground && count ? '#f5f5f5' : 'unset',
      alignSelf: 'center',
      height: '100%',
      alignItems: 'center',
    }}
  >
    <span className={classes.info_block}>
      <div
        onClick={(count && !noLink && onClick) || undefined}
        style={
          (count &&
            !noLink && {
              cursor: (onClick && 'pointer') || undefined,
              color: '#2688ec',
              textDecoration: (onClick && 'underline') || undefined,
            }) ||
          undefined
        }
      >
        {count.toLocaleString()}
      </div>
      <Text style={{}} color={theme.color.neutral2}>
        {type}
      </Text>
    </span>
  </Flex>
);

const getSyncTypeInfo = (syncType?: string) => {
  if (syncType && syncType.toLowerCase() in syncTypeInfo) {
    return syncTypeInfo[syncType.toLowerCase()];
  }
  return syncTypeInfo.incremental;
};

// TODO: Separate source and server components
const MainContainer: React.FC<MainContainerPropsProps> = ({
  rejected,
  succeeded,
  startedAt,
  finishedAt,
  syncType,
  deltas,
  totalCount,
  triggerSync,
  type,
  arrow,
  error,
  state,
  progress,
  showResultFeedback,
  stateObj,
  onClickErrors,
  onClickMetric,
}: MainContainerPropsProps) => {
  const Container = arrow ? ArrowContainer : BaseContainer;
  const syncing = isSyncing(startedAt, finishedAt);
  const hasError = !!error;
  const { source } = useCloudSourceInfo();
  const paused = !source?.enabled && syncing;
  const syncBehaviourMode = source.config?.syncBehaviour?.mode;

  // source side
  const recordsSentToDestination =
    stateObj && stateObj.type === SyncResult.RecordsSentToDestination && syncing;
  let syncResult: SyncResult;
  let tooltipResultText: string;
  let syncResultIcon;
  let stateComp = null;

  if (hasError) {
    syncResult = SyncResult.Error;
    tooltipResultText = error;
    syncResultIcon = <WarningTwoTone twoToneColor="#EDCB74" style={{ fontSize: 24 }} />;
    stateComp = 'Sync failed';
  } else if (paused) {
    syncResult = SyncResult.Paused;
    tooltipResultText = 'Sync is paused';
    syncResultIcon = <PauseCircleTwoTone style={{ fontSize: '24px' }} twoToneColor="#ff5050" />;
    stateComp = 'Suspended';
  } else if (recordsSentToDestination) {
    syncResult = SyncResult.RecordsSentToDestination;
    tooltipResultText = stateObj.info;
    syncResultIcon = (
      <FontAwesomeIcon
        icon={solid('circle-check')}
        color={theme.color.success}
        style={{ fontSize: 24 }}
      />
    );
    stateComp = <span className={classes.loading}>{stateObj.info}</span>;
  } else if (syncing) {
    syncResult = SyncResult.Syncing;
    tooltipResultText = 'Sync in progress';
    syncResultIcon = <SyncOutlined style={{ fontSize: '1.5em', color: '#999999' }} spin />;
    if (progress === 'processing') {
      stateComp = <span className={classes.loading}>{state}</span>;
    } else {
      stateComp = state;
    }
  } else {
    syncResult = SyncResult.Success;
    tooltipResultText = 'Success';
    syncResultIcon = (
      <FontAwesomeIcon
        icon={solid('circle-check')}
        color={theme.color.success}
        style={{ fontSize: 24 }}
      />
    );
    if (succeeded) {
      stateComp = 'Sync completed';
    } else {
      stateComp = state;
    }
  }

  // server side

  const { showServerSucceeded } = SyncBehaviourModeFactory(syncBehaviourMode);

  const getServerSucceeded = () => {
    let serverSucceeded;
    if (syncing && !paused) {
      serverSucceeded = <CountUp preserveValue start={0} end={succeeded} />;
    } else {
      serverSucceeded = succeeded.toLocaleString();
    }
    return serverSucceeded;
  };

  return (
    <DropShadow>
      <Container>
        <Flex alignItems="center">
          {showResultFeedback ? (
            <Tooltip title={tooltipResultText}>
              <div style={{ marginRight: 16 }}>{syncResultIcon}</div>
            </Tooltip>
          ) : null}

          <div>
            {showResultFeedback ? <ResultBorder result={syncResult} /> : null}
            <Flex alignItems="flex-start" flexDirection="column">
              {source?.config?.syncBehaviour?.mode && type === SYNC_DIVIDER.SOURCES && (
                <LabelDiv style={{ color: 'black' }}>{stateComp}</LabelDiv>
              )}
              {type === SYNC_DIVIDER.SERVER && showServerSucceeded(syncing, succeeded) && (
                <div>
                  <LabelDiv style={{ color: '#2688ec' }}>
                    {getServerSucceeded()} deltas succeeded
                  </LabelDiv>
                </div>
              )}
            </Flex>
            <div>
              <Text
                color={theme.color.neutral2}
                style={{
                  visibility: type === 'server' && syncing ? 'hidden' : 'unset',
                }}
              >
                {type === 'server' ? (
                  <span>Finished {!syncing && <DateFormat date={finishedAt!} />}</span>
                ) : (
                  <span>
                    Started <DateFormat date={startedAt} />
                  </span>
                )}
              </Text>
            </div>
            <div>
              {type === 'server' && (
                <Text
                  color={theme.color.neutral2}
                  style={{
                    visibility: type === 'server' && syncing ? 'hidden' : 'unset',
                  }}
                >
                  Duration{' '}
                  {humanizeRunDatesDuration({
                    startedAt,
                    finishedAt: !syncing ? finishedAt : moment().toISOString(),
                  })}
                </Text>
              )}
              {type === 'sources' && (
                <Flex alignItems="center">
                  <Text color={theme.color.neutral2}>{startCase(syncType)} sync</Text>
                  <Tooltip title={getSyncTypeInfo(syncType)}>
                    <InfoCircleOutlined style={{ marginLeft: 4, color: theme.color.neutral2 }} />
                  </Tooltip>
                  {type === 'sources' && (
                    <div
                      style={{
                        marginLeft: 4,
                        padding: '0 2px',
                        backgroundColor: '#f5f5f5',
                        lineHeight: '12px',
                      }}
                    >
                      <Text fontWeight="500" fontSize="10px">
                        {' '}
                        {triggerSync?.toUpperCase()}
                      </Text>
                    </div>
                  )}
                </Flex>
              )}
            </div>
          </div>
        </Flex>
        <Flex style={{ marginLeft: 'auto' }}>
          {(type === SYNC_DIVIDER.SOURCES && !!source?.config?.syncBehaviour?.mode && (
            <SourceInfoBlock
              totalCount={totalCount || 0}
              onClickMetric={onClickMetric}
              deltas={deltas}
            />
          )) ||
            null}
          {(onClickMetric && type === SYNC_DIVIDER.SERVER && (
            <InfoBlock
              count={succeeded || 0}
              type="Successful"
              onClick={() => {
                onClickMetric(METRIC_TYPE.SUCCESSFUL);
              }}
            />
          )) ||
            null}
          <InfoBlock
            count={rejected}
            onClick={onClickErrors && (() => onClickErrors(type))}
            type={type === 'server' ? 'Failed' : 'Invalid'}
          />
        </Flex>
      </Container>
    </DropShadow>
  );
};

const Report: React.FC<ReportProps> = ({
  report,
  operationMetrices,
  onClickRejected,
  onClickRecords,
}: ReportProps) => {
  const syncing = isSyncing(report.startedAt, report.finishedAt);

  useEffect(() => {
    if (operationMetrices) {
      if (operationMetrices.type === METRIC_TYPE.DELTAS && onClickSourcesRecords) {
        onClickSourcesRecords(METRIC_TYPE.DELTAS);
      } else if (operationMetrices.type === METRIC_TYPE.SUCCESSFUL && onClickServerRecords) {
        onClickServerRecords(METRIC_TYPE.SUCCESSFUL);
      }
    }
  }, [syncing, report.opMetrics]);

  const onClickSourcesRecords =
    onClickRecords &&
    ((type: string) =>
      onClickRecords({
        inserts: report.opMetrics?.total?.inserts || 0,
        updates: report.opMetrics?.total?.updates || 0,
        deletes: report.opMetrics?.total?.deletes || 0,
        jobRunId: report.jobRunId,
        taskRunId: report.taskRunId,
        syncType: report.syncType,
        type,
      }));

  const onClickServerRecords =
    onClickRecords &&
    ((type: string) =>
      onClickRecords({
        inserts: report.opMetrics?.successful?.inserts || 0,
        updates: report.opMetrics?.successful?.updates || 0,
        deletes: report.opMetrics?.successful?.deletes || 0,
        jobRunId: report.jobRunId,
        taskRunId: report.taskRunId,
        syncType: report.syncType,
        type,
        syncing,
      }));

  const onClickErrors = (type: 'sources' | 'server') =>
    onClickRejected({
      jobRunId: report.jobRunId,
      taskRunId: report.taskRunId,
      startedAt: report.startedAt,
      type,
    });
  return (
    <Flex style={{ width: '100%' }}>
      <div style={{ width: '100%', marginRight: '12px' }}>
        <MainContainer
          showResultFeedback
          arrow
          type="sources"
          totalCount={report?.sourceStats?.totalCount || 0}
          error={report.error}
          deltas={report.sourceStats?.changed || 0}
          succeeded={report.sourceStats?.succeeded || 0}
          rejected={report.sourceStats?.invalid || 0}
          startedAt={report.startedAt}
          finishedAt={report.finishedAt}
          triggerSync={report.triggerSync || 'auto'}
          syncType={report.syncType || 'full'}
          state={report.state}
          stateObj={report.stateObj}
          progress={report?.progress}
          onClickErrors={onClickErrors}
          onClickMetric={onClickSourcesRecords}
        />
      </div>
      <div style={{ width: '100%' }}>
        <MainContainer
          type="server"
          error={report.error}
          succeeded={report?.serverStats?.succeeded || 0}
          rejected={report?.serverStats?.rejected || 0}
          startedAt={report?.startedAt}
          finishedAt={report?.finishedAt}
          state={report.state}
          totalCount={report?.serverStats?.totalCount || 0}
          onClickErrors={onClickErrors}
          onClickMetric={onClickServerRecords}
        />
      </div>
    </Flex>
  );
};

export const ReportHeader = ({ sourceName, destinationName }: FixMe) => (
  <Flex style={{ width: '100%' }}>
    <div style={{ width: '100%', marginRight: '12px' }}>
      <div style={{ marginBottom: 16 }}>
        <Text fontSize="16px" color={theme.color.neutral2}>
          Sent from {sourceName}
        </Text>
      </div>
    </div>
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <Text fontSize="16px" color={theme.color.neutral2}>
          To {destinationName}
        </Text>
      </div>
    </div>
  </Flex>
);
export default Report;
